'use client';

import { useEffect, useState } from 'react';
import { Hash } from 'lucide-react';

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface ResourceTableOfContentsProps {
  content: string;
}

export default function ResourceTableOfContents({
  content,
}: ResourceTableOfContentsProps) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  // Extract h1 and h2 headings from content and add IDs to rendered headings
  useEffect(() => {
    if (!content) return;

    // Wait for content to be rendered
    const timeoutId = setTimeout(() => {
      const contentContainer = document.querySelector('[data-resource-content]');
      if (!contentContainer) return;

      const h1Elements = contentContainer.querySelectorAll('h1');
      const h2Elements = contentContainer.querySelectorAll('h2');

      const extractedHeadings: Heading[] = [];

      // Process h1 headings
      h1Elements.forEach((el) => {
        const text = el.textContent?.trim() || '';
        if (text) {
          const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          // Ensure unique ID
          let uniqueId = id;
          let counter = 1;
          while (document.getElementById(uniqueId)) {
            uniqueId = `${id}-${counter}`;
            counter++;
          }
          el.id = uniqueId;
          extractedHeadings.push({ id: uniqueId, text, level: 1 });
        }
      });

      // Process h2 headings
      h2Elements.forEach((el) => {
        const text = el.textContent?.trim() || '';
        if (text) {
          const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
          // Ensure unique ID
          let uniqueId = id;
          let counter = 1;
          while (document.getElementById(uniqueId)) {
            uniqueId = `${id}-${counter}`;
            counter++;
          }
          el.id = uniqueId;
          extractedHeadings.push({ id: uniqueId, text, level: 2 });
        }
      });

      setHeadings(extractedHeadings);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [content]);

  // Track active heading on scroll
  useEffect(() => {
    const handleScroll = () => {
      const headingElements = headings.map((h) => document.getElementById(h.id)).filter(Boolean) as HTMLElement[];
      
      if (headingElements.length === 0) return;

      let current = '';
      for (const element of headingElements) {
        const rect = element.getBoundingClientRect();
        if (rect.top <= 100) {
          current = element.id;
        } else {
          break;
        }
      }

      if (current) {
        setActiveId(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  const scrollToHeading = (id: string) => {
    // Try to find the element, wait a bit if not found (content might still be rendering)
    const findAndScroll = () => {
      const element = document.getElementById(id);
      if (element) {
        // Get the sticky header height if it exists
        const header = document.querySelector('[data-sticky-header]');
        const headerHeight = header ? header.getBoundingClientRect().height : 20;
        
        // Calculate position relative to document
        const rect = element.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const elementTop = rect.top + scrollTop;
        const offsetPosition = elementTop - headerHeight - 20; // 20px extra padding

        // Scroll to the calculated position
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth',
        });
        
        // Update active state immediately
        setActiveId(id);
        return true;
      }
      return false;
    };

    // Try immediately
    if (!findAndScroll()) {
      // If element not found, wait a bit and try again (content might still be rendering)
      setTimeout(() => {
        findAndScroll();
      }, 200);
    }
  };

  if (headings.length === 0) {
    return null;
  }

  return (
    <div className="border rounded-lg p-4 bg-card">
      <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <Hash className="w-4 h-4" />
        Table of Contents
      </h3>
      <nav className="space-y-1">
        {headings.map((heading) => (
          <button
            key={heading.id}
            onClick={() => scrollToHeading(heading.id)}
            className={`block w-full text-left text-sm py-1.5 px-2 rounded transition-colors ${
              heading.level === 1
                ? 'font-semibold'
                : 'font-normal pl-4 text-muted-foreground'
            } ${
              activeId === heading.id
                ? 'bg-primary/10 text-primary'
                : 'hover:bg-muted text-foreground'
            }`}
          >
            {heading.text}
          </button>
        ))}
      </nav>
    </div>
  );
}

